'use client';

import { format } from 'date-fns';
import { useRouter } from 'next/navigation';

import { Button, Card, Separator, Skeleton } from '@mokoboko/components';
import { Wallet, Calendar } from 'lucide-react';
import { formatAmount } from '@/lib/utils';
import { getTopupCreditsTotal } from '@/helpers/credits';
import { Order } from '@/lib/types';
import { useAuth } from '@/contexts/auth';
import { ReceiptRow, ReceiptStatusHeader } from './receipt-rows';

const getAttachmentLabel = (
  sourceType: 'PACK' | 'SUBSCRIPTION' | undefined
): string => {
  if (sourceType === 'PACK') return 'Added to your pack';
  if (sourceType === 'SUBSCRIPTION') return 'Added to your membership';
  return 'Added to your account';
};

export const TopupOrderReceipt = ({ order }: { order: Order }) => {
  const router = useRouter();
  const { isAuthenticated } = useAuth();

  const { topup_order_items = [] } = order;

  const totalCredits = getTopupCreditsTotal(topup_order_items);

  const topupName = topup_order_items[0]?.topup?.name ?? 'Top-up';

  const expiresAt = topup_order_items[0]?.topup_code?.expires_at;
  const expiryLabel = expiresAt
    ? `Expires on ${format(new Date(expiresAt), 'd MMM, yyyy')}`
    : 'Never expires';

  return (
    <div className="bg-white">
      <ReceiptStatusHeader status="confirmed" title={topupName} />

      <Separator className="my-5 sm:my-6" />

      <div className="flex flex-col gap-4">
        <ReceiptRow
          icon={Wallet}
          title={`${totalCredits} ${totalCredits === 1 ? 'credit' : 'credits'} added`}
        >
          <p className="mt-1 text-sm text-gray-600">
            Paid:{' '}
            {formatAmount(
              order.final_amount ?? order.total_amount,
              order.currency
            )}
          </p>
        </ReceiptRow>

        <ReceiptRow
          icon={Calendar}
          title={getAttachmentLabel(topup_order_items[0]?.source_type)}
        >
          <p className="mt-1 text-sm text-gray-600">{expiryLabel}</p>
        </ReceiptRow>
      </div>

      <Separator className="my-6" />

      <div className="flex flex-col gap-3">
        <p className="text-base font-semibold text-gray-900">
          {totalCredits === 1
            ? 'Your credit is ready'
            : 'Your credits are ready'}
        </p>
        <Button
          onClick={() => router.push('/classes')}
          size="lg"
          className="w-full justify-center"
        >
          Book a class
        </Button>
        <Button
          variant="secondary-gray"
          onClick={() =>
            router.push(isAuthenticated ? '/dashboard/credits' : '/')
          }
          size="lg"
          className="w-full justify-center"
        >
          View my credits
        </Button>
      </div>
    </div>
  );
};

export const TopupReceiptSkeleton = () => {
  return (
    <Card contentClassName="p-5 sm:p-6 flex flex-col gap-5 sm:gap-6">
      <div className="flex items-center justify-between gap-4">
        <Skeleton isRounded className="!h-11 w-[140px] sm:!h-10 sm:w-[124px]" />
      </div>

      <Separator />

      <div className="flex flex-col items-center">
        <Skeleton isCircle height={80} />
        <div className="mt-3">
          <Skeleton text="body-sm" width={80} />
        </div>
        <div className="mt-1.5">
          <Skeleton text="h2" width={208} />
        </div>
      </div>

      <Separator />

      <div className="flex flex-col gap-4">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton isCircle height={48} />
            <div>
              <Skeleton text="body-sm" width={160} />
              <div className="mt-1">
                <Skeleton text="body-sm" width={96} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Separator />

      <div className="flex flex-col gap-3">
        <Skeleton text="body" width={192} />
        <Skeleton isRounded height={44} width="100%" />
        <Skeleton isRounded height={44} width="100%" />
      </div>
    </Card>
  );
};
